import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Item 2 — debounced user search hook.
 *
 * Returns `{ users, loading, hint }` where `hint` is a small nudge
 * shown alongside results reminding the user to search by @handle
 * for exact matches (names can repeat).
 *
 * A minimum query length of 2 characters keeps the endpoint from
 * being hammered on every single-character keystroke.
 */
export default function useUserSearch(query, { debounceMs = 250, enabled = true, limit = 10 } = {}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) { setUsers([]); return; }
    const q = (query || "").trim();
    if (q.length < 2) { setUsers([]); return; }
    const ctrl = new AbortController();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/search", {
          params: { q, limit },
          signal: ctrl.signal,
        });
        setUsers(data.users || []);
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          console.error("[useUserSearch] failed:", err);
        }
      } finally { setLoading(false); }
    }, debounceMs);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query, enabled, debounceMs, limit]);

  return {
    users,
    loading,
    // Rendered by the caller alongside the results list.
    hint: "Search by @handle for an exact match — names can repeat.",
  };
}
